import Form from 'antd/es/form';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  ProxyServer,
  ProxyServerFormFields,
  ProxyServerFormInputs,
  ProxyServersQueryKeys,
  useAddProxyServerAddressesMutation,
  useGetOneByIdProxyServersQuery,
  useUpdateProxyServersMutation
} from '@/modules/ProxyServers';

import { ProxyServersFormView } from './ProxyServersFormView';
import { proxyServersEditFormValidationSchema } from './validation';

interface Props {
  proxyServerId: string;
}

const remapNewIpFieldErrors = (
  errors: FormFieldsErrors<typeof ProxyServerFormInputs>,
  offset: number
): FormFieldsErrors<typeof ProxyServerFormInputs> => {
  if (!errors || !offset) return errors;

  const remapped: Record<string, string> = {};

  for (const [key, message] of Object.entries(errors)) {
    const match = /^ip\[(\d+)]$/.exec(key);

    remapped[match ? `ip[${ Number(match[1]) + offset }]` : key] = message;
  }

  return remapped as FormFieldsErrors<typeof ProxyServerFormInputs>;
};

export const ProxyServersEditFormContainer: FC<Props> = memo(({ proxyServerId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<ProxyServerFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof ProxyServerFormInputs>>(null);

  const hostingIdFormValue = Form.useWatch<string>(ProxyServerFormInputs.HostingId, form);

  const {
    data: proxyServerData,
    error: proxyServerError,
    isLoading: isProxyServerLoading,
    isError: isProxyServerError
  } = useGetOneByIdProxyServersQuery(proxyServerId)({
    enabled: !!proxyServerId
  });

  const { mutate: updateProxyServerMutation, isLoading: updateProxyServerIsLoading } = useUpdateProxyServersMutation(
    proxyServerId
  )({
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof ProxyServerFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const { mutate: addAddressesMutation, isLoading: addAddressesIsLoading } = useAddProxyServerAddressesMutation(
    proxyServerId
  )({
    onError: (err) => {
      setFormErrors(
        remapNewIpFieldErrors(
          extractFormErrors<typeof ProxyServerFormInputs>(err),
          proxyServerData?.addresses.length ?? 0
        )
      );
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: ProxyServerFormFields = {
    name: proxyServerData?.name,
    hostingAccountId: proxyServerData?.hostingAccount?.id,
    hostingId: proxyServerData?.hosting?.id,
    ips: proxyServerData?.addresses.map((address) => address.ip) ?? [],
    ip: undefined,
    password: undefined
  };

  const handleSuccess = useCallback(async (data: ProxyServer): Promise<void> => {
    appMessageApi.success('Проксі-сервер успішно змінено!');

    await queryClient.refetchQueries([ProxyServersQueryKeys.GetAll]);
    await queryClient.refetchQueries([ProxyServersQueryKeys.GetOneById, proxyServerId]);

    navigate(AppRoutes.ProxyServersById.replace(':proxyServerId', data.id));
  }, [proxyServerId]);

  const handleFinish = useCallback((values: ProxyServerFormFields): void => {
    setFormErrors(null);

    const existingIps = proxyServerData?.addresses.map((address) => address.ip) ?? [];
    const newIps = ((values.ips as (string | undefined)[] | undefined) ?? [])
      .filter((ip): ip is string => Boolean(ip))
      .filter((ip) => !existingIps.includes(ip));

    updateProxyServerMutation(
      {
        name: values.name,
        hostingId: values.hostingId,
        hostingAccountId: values.hostingAccountId,
        ...(values.password ? { password: values.password } : {})
      },
      {
        onSuccess: (data) => {
          if (newIps.length) {
            addAddressesMutation({ ips: newIps }, {
              onSuccess: () => {
                appMessageApi.info('Нові адреси додано в чергу на налаштування.');

                handleSuccess(data);
              }
            });
          } else {
            handleSuccess(data);
          }
        }
      }
    );
  }, [handleSuccess]);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.ProxyServersById.replace(':proxyServerId', proxyServerId));
  }, [proxyServerId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <FallbackedLayout
      isLoading={ isProxyServerLoading }
      isError={ isProxyServerError }
      error={ proxyServerError }
    >
      { () => (
        <ProxyServersFormView
          formName="proxy-server-edit"
          formType="edit"
          form={ form }
          formErrors={ formErrors }
          initialValues={ initialValues }
          isFormLoading={ updateProxyServerIsLoading || addAddressesIsLoading }
          validationSchema={ proxyServersEditFormValidationSchema }
          hostingIdFormValue={ hostingIdFormValue }
          existingIpsCount={ proxyServerData?.addresses.length ?? 0 }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      ) }
    </FallbackedLayout>
  );
});
